// Shared mutable state for the widget. One ChatStateStore instance lives
// at the root <emma-chat> element and is provided through Lit Context to
// every descendant component (T08-T11 bubbles, T07 inputs, T09 quick-chips).
//
// Design notes (per plan.md Selected Approach Dimension 4):
//
//   - Single Context value: the store instance itself (not the state object).
//     The instance reference stays stable across the widget's lifetime; only
//     `state` field changes. Consumers subscribe via subscribe(listener) +
//     a small Lit ReactiveController (StateConsumerController) that calls
//     host.requestUpdate() on each notification.
//
//   - Mutations go through methods, not direct assignment. Lets us add
//     invariants later (e.g. "lastMode = 'sovereign_search' implies notice
//     gets cleared") in one place.
//
//   - state field is replaced by spread on each mutation (immutable update
//     pattern). Consumers can use Object.is() reference checks to memoize
//     if needed.
//
// The 7 mutable globals from the legacy monolith (audit FRONTEND-AUDIT.md
// §3.1, §11.1) — turnHistory, lastResolvedParams, lastMode, lastPickedIDs,
// isSubmitting, signupHintShown, submitTimestamp — plus capabilities map
// onto this single store.

import type {
  ChatMode,
  CapabilitiesResponse,
  SearchParams,
  SSEData,
} from '../types/index.js';

/**
 * One conversation turn: the user's query plus the SSE response data.
 * Stored in `turnHistory` so later turns (refine, pick_from_results) can
 * reference earlier results by index.
 */
export interface TurnEntry {
  /** User's raw query text. Empty string allowed for synthetic system-only
   *  turns (see isSyntheticSystem). */
  query: string;
  /** Full SSE event accumulator from the response. */
  sseData: SSEData;
  /** Resolved search params from the result event (refine source for next turn). */
  resolvedParams: SearchParams | null;
  /** True for carry-deploy / navigate entry turns the host injected via
   *  startWithConfig / startWithNavigation. Renders without a user bubble
   *  (no question was actually typed by the user). */
  isSyntheticSystem?: boolean;
  /** Optimistic in-flight turn: the user's question (e.g. a clicked chip) is
   *  shown as a user bubble + a thinking emma bubble immediately, before the
   *  SSE response lands. finalizeTurn replaces it in place with the real
   *  sseData (replaceLastTurn). sseData is empty {} while pending. */
  pending?: boolean;
  /** When set, this turn is a navigate-mode prompt (K8s carry-deploy
   *  handoff). Renders a two-CTA bubble: "Continue to wizard" (dispatches
   *  emma-navigate-intent) and "Refine here" (focuses refine input). */
  carryNavigatePrompt?: {
    workload: 'kubernetes';
    utmSource?: string;
  };
  /** Display-only system/status event injected by the host via
   *  <emma-chat>.pushEvent — e.g. a portal deploy-wizard toast mirrored into
   *  the chat (its TEXT, not its toast styling) so the deploy context lives in
   *  the thread. Rendered with the SAME emma bubble as a normal answer. Carries
   *  NO sseData and never sets resolvedParams, so it is invisible to
   *  refine/grounding state and is never forwarded to the BFF (the search
   *  request sends only query/portalContext/previousParams, not message
   *  history).
   *
   *  Optional `action` renders a link-style button under the text; clicking it
   *  dispatches a bubbling composed CustomEvent named `action.event` from the
   *  <emma-chat> host (same out-event pattern as emma-deploy-click), so the
   *  HOST decides what happens — e.g. the portal's gated-deploy notice offers
   *  "Open a support ticket" and the portal opens its support drawer on the
   *  event (QA-2 #22: offer, don't auto-open). Display-only like the text:
   *  never sent to the BFF. */
  systemEvent?: {
    text: string;
    action?: { label: string; event: string };
  };
  // NOTE: systemEvent payloads from OUTSIDE the widget (host pushEvent AND
  // hydrated transcripts) must pass through boundSystemEventAction /
  // boundSystemEvent below — never store them verbatim.
  /** Documentation card injected by the host via <emma-chat>.showDocCard —
   *  the portal's Help entry ("?" in the header / a deep-link from an aside)
   *  resolves the article for the current screen from the docs manifest and
   *  drops it into the transcript as a first-class turn.
   *
   *  Deliberately NOT an LLM turn: opening help costs zero model calls (see
   *  docs-import-plan.md, arbitration #2 — latency, tokens, and the risk of an
   *  LLM paraphrasing instruction steps wrong). Everything rendered here comes
   *  from the host's manifest data; the excerpt is a progressive upgrade
   *  fetched from the BFF docs proxy, and the card renders fine without it.
   *
   *  Like systemEvent, this carries NO sseData and never sets resolvedParams —
   *  invisible to refine/grounding and never sent to the BFF. Untrusted at
   *  intake (host argument AND hydrated transcript) → boundDocCard. */
  docCard?: DocCardEntry;
  /** True for turns re-hydrated from a carried transcript (dialog-carry
   *  Phase 1, <emma-chat>.hydrateTranscript). These are already-answered turns
   *  replayed verbatim, NOT live searches — the turn component suppresses the
   *  reveal-animation theatre (scanning/cycling/found) for them so the portal
   *  doesn't fake a loading sequence on an answer the user already saw. */
  hydrated?: boolean;
}

/** Max characters of a systemEvent bubble text (pushEvent AND hydrate). */
export const SYSTEM_EVENT_TEXT_MAX = 300;

/** One follow-up chip under a doc-card. Same {label, prompt} shape the
 *  consultative bubble already feeds <emma-chat-quick-chips>. */
export interface DocCardChip {
  label: string;
  prompt: string;
}

/** Documentation card payload — see TurnEntry.docCard. */
export interface DocCardEntry {
  /** Manifest slug ('' = docs home). Also the BFF excerpt lookup key. */
  slug: string;
  /** Article title, shown as the card headline. */
  title: string;
  /** Absolute https URL of the article on the docs site — the target of both
   *  [Open in docs ↗] and [Copy link]. */
  url: string;
  /** Manifest blurb. The offline-safe body: rendered when no excerpt (yet). */
  description?: string;
  /** Prose excerpt from the BFF docs proxy — replaces `description` in the
   *  card body once it lands. Absent when the BFF is down/disabled. */
  excerpt?: string;
  /** Follow-up chips rendered under the card. Emitted with origin='help', so
   *  a click routes to the grounding/RAG lane rather than a config search. */
  chips?: DocCardChip[];
  /** Correlation id, set by showDocCard, used to find this exact turn again
   *  when the async excerpt lands (the user may have added turns meanwhile). */
  token?: string;
}

export const DOC_CARD_TITLE_MAX = 160;
export const DOC_CARD_TEXT_MAX = 600;
export const DOC_CARD_CHIPS_MAX = 4;
export const DOC_CARD_CHIP_LABEL_MAX = 60;
export const DOC_CARD_CHIP_PROMPT_MAX = 200;

/** Slug shape mirrored from the BFF (docs_manifest.go docsSlugRe): lowercase
 *  path segments, no dots (no `..`), no scheme, no leading slash. '' = home. */
const DOC_CARD_SLUG_RE = /^[a-z0-9][a-z0-9/_-]{0,120}$/;

/** Bound an UNTRUSTED doc-card (host showDocCard argument, or a field of a
 *  resumed transcript — dialog-carry blobs are attacker-influencable). Returns
 *  a fresh, capped entry, or undefined when the payload can't be rendered
 *  safely. Rules:
 *   - slug: '' or the manifest slug shape (keeps a crafted slug out of the BFF
 *     query and out of the logs),
 *   - title: required, trimmed, capped,
 *   - url: required, ABSOLUTE http(s) only — this is what [Open in docs] links
 *     to and what [Copy link] writes to the clipboard, so a `javascript:` or
 *     `data:` URL must never survive intake,
 *   - description/excerpt: trimmed + capped,
 *   - chips: at most DOC_CARD_CHIPS_MAX, each with a non-empty label+prompt. */
export function boundDocCard(
  doc:
    | {
        slug?: unknown;
        title?: unknown;
        url?: unknown;
        description?: unknown;
        excerpt?: unknown;
        chips?: unknown;
        token?: unknown;
      }
    | null
    | undefined,
): DocCardEntry | undefined {
  if (!doc || typeof doc !== 'object') return undefined;

  const slug = (doc.slug ?? '').toString().trim().replace(/^\/+|\/+$/g, '');
  if (slug !== '' && !DOC_CARD_SLUG_RE.test(slug)) return undefined;

  const title = (doc.title ?? '').toString().trim().slice(0, DOC_CARD_TITLE_MAX);
  if (!title) return undefined;

  const url = boundDocURL(doc.url);
  if (!url) return undefined;

  const entry: DocCardEntry = { slug, title, url };
  const description = (doc.description ?? '').toString().trim().slice(0, DOC_CARD_TEXT_MAX);
  if (description) entry.description = description;
  const excerpt = (doc.excerpt ?? '').toString().trim().slice(0, DOC_CARD_TEXT_MAX);
  if (excerpt) entry.excerpt = excerpt;
  const chips = boundDocCardChips(doc.chips);
  if (chips.length > 0) entry.chips = chips;
  // Deliberately NOT carried: `token` is this widget's own excerpt-correlation
  // handle, minted by showDocCard AFTER bounding. Preserving an incoming one lets
  // a crafted transcript claim `dc1` — which the counter, restarting at 0, then
  // mints for the first real card, so its excerpt would land on the wrong turn.
  return entry;
}

/** http(s)-only absolute URL bound. Returns the normalized href or undefined. */
function boundDocURL(raw: unknown): string | undefined {
  const s = (raw ?? '').toString().trim();
  if (!s) return undefined;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return undefined; // relative URLs are rejected: the docs origin is the
    // host's to resolve, the widget must not guess it from its own page.
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') return undefined;
  return u.href;
}

export function boundDocCardChips(raw: unknown): DocCardChip[] {
  if (!Array.isArray(raw)) return [];
  const out: DocCardChip[] = [];
  for (const c of raw) {
    if (!c || typeof c !== 'object') continue;
    const src = c as { label?: unknown; prompt?: unknown };
    const label = (src.label ?? '').toString().trim().slice(0, DOC_CARD_CHIP_LABEL_MAX);
    const prompt = (src.prompt ?? '').toString().trim().slice(0, DOC_CARD_CHIP_PROMPT_MAX);
    if (!label || !prompt) continue;
    out.push({ label, prompt });
    if (out.length >= DOC_CARD_CHIPS_MAX) break;
  }
  return out;
}

/** Event names a systemEvent action may NOT dispatch: the widget's own in-band
 *  handler names plus the established host-facing contracts. Action events are
 *  re-dispatched from the <emma-chat> HOST (so they can't reach the in-band
 *  handlers attached inside the shadow tree), but the denylist keeps a crafted
 *  action from SPOOFING a contract event (e.g. a null-detail emma-deploy-click)
 *  toward host-page listeners. Includes the internal relay name itself. */
export const SYSTEM_EVENT_ACTION_RESERVED = new Set([
  'emma-card-click',
  'emma-carry-refine-here',
  'emma-chip-click',
  'emma-clear-conversation',
  'emma-deploy-click',
  'emma-input-submit',
  'emma-navigate-intent',
  'emma-refine-submit',
  'emma-refine-trigger-click',
  'emma-stop-thinking',
  'emma-turn-revealed',
  'emma-system-event-action',
]);

const SYSTEM_EVENT_ACTION_NAME_RE = /^emma-[a-z0-9-]+$/;

/** Bound an UNTRUSTED systemEvent action (host pushEvent argument, or a field
 *  of a resumed transcript — dialog-carry blobs are attacker-influencable, the
 *  stash endpoint is anonymous). Returns a fresh {label, event} when valid;
 *  undefined otherwise. Rules: label non-empty after trim, capped at 60; event
 *  in the widget out-event namespace (emma-[a-z0-9-]+) and NOT a reserved
 *  in-band/contract name. */
export function boundSystemEventAction(
  action: { label?: unknown; event?: unknown } | null | undefined,
): { label: string; event: string } | undefined {
  if (!action || typeof action !== 'object') return undefined;
  const label = (action.label ?? '').toString().trim().slice(0, 60);
  const event = (action.event ?? '').toString().trim();
  if (!label || !SYSTEM_EVENT_ACTION_NAME_RE.test(event)) return undefined;
  if (SYSTEM_EVENT_ACTION_RESERVED.has(event)) return undefined;
  return { label, event };
}

/** Bound a whole UNTRUSTED systemEvent (hydrateTranscript path): text trimmed
 *  and capped, action re-validated. Returns undefined for an empty text so a
 *  junk entry degrades to a plain (non-system) turn shape upstream. */
export function boundSystemEvent(
  ev: { text?: unknown; action?: { label?: unknown; event?: unknown } } | null | undefined,
): { text: string; action?: { label: string; event: string } } | undefined {
  if (!ev || typeof ev !== 'object') return undefined;
  const text = (ev.text ?? '').toString().trim().slice(0, SYSTEM_EVENT_TEXT_MAX);
  if (!text) return undefined;
  return { text, action: boundSystemEventAction(ev.action) };
}

/**
 * Read-only snapshot of widget state at a point in time. Replaced on
 * every mutation; do not mutate fields directly — use store methods.
 */
export interface ChatState {
  /** Feature flags from BFF /api/hero/capabilities. null until first fetch. */
  capabilities: CapabilitiesResponse | null;
  /** Conversation history. Empty before first turn. Reset on resetToInput. */
  turnHistory: TurnEntry[];
  /** Resolved search params from the most recent result event. */
  lastResolvedParams: SearchParams | null;
  /** Mode from the most recent result event. Drives next-turn behavior. */
  lastMode: ChatMode;
  /** Cards picked by the LLM in the most recent pick_from_results event. */
  lastPickedIDs: string[];
  /** True between submitQuery start and finalizeTurn end. Guards against double-submit. */
  isSubmitting: boolean;
  /** Soft-gate hint shown at least once this session. Prevents re-showing. */
  signupHintShown: boolean;
  /** Date.now() at the most recent submitQuery start. Drives "updated N min ago" UI. */
  submitTimestamp: number | null;
  /** ID (`tXcY`) of the user-selected card. Only one card can be selected
   *  across the entire conversation. null = no manual selection (best-price
   *  fallback applies per turn). The selected card's ctaUrl is used by the
   *  bottom CTA when present. */
  selectedCardId: string | null;
  /** Carry-deploy mode marker set by startWithConfig / startWithNavigation.
   *   - 'config'   = VM/Spot card loaded from landing — bottom-CTA immediate,
   *                  demo typewriter suppressed.
   *   - 'navigate' = K8s navigate-mode — bubble with two CTAs, no card.
   *   - null       = standard search/refine flow (anonymous landing default).
   *  Cleared on first trusted user submit / refine to revert to standard timing. */
  carryDeployMode: 'config' | 'navigate' | null;
}

/** Subscriber callback fired on every state mutation. */
export type StateListener = () => void;

function initialState(): ChatState {
  return {
    capabilities: null,
    turnHistory: [],
    lastResolvedParams: null,
    lastMode: '',
    lastPickedIDs: [],
    isSubmitting: false,
    signupHintShown: false,
    submitTimestamp: null,
    selectedCardId: null,
    carryDeployMode: null,
  };
}

export class ChatStateStore {
  /** Read-only snapshot. Replaced on every mutation; never mutated in place. */
  state: ChatState;

  private listeners = new Set<StateListener>();

  constructor() {
    this.state = initialState();
  }

  /**
   * Subscribe to state changes. Returns an unsubscribe function. Lit
   * components should subscribe via StateConsumerController which handles
   * the controller lifecycle automatically.
   */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Number of subscribers — useful for tests. */
  get subscriberCount(): number {
    return this.listeners.size;
  }

  // --- Mutators ---

  setCapabilities(caps: CapabilitiesResponse): void {
    this.state = { ...this.state, capabilities: caps };
    this.notify();
  }

  /** Append a turn. Used by finalizeTurn after each successful SSE response. */
  pushTurn(entry: TurnEntry): void {
    this.state = {
      ...this.state,
      turnHistory: [...this.state.turnHistory, entry],
    };
    this.notify();
  }

  /** Replace the most recent turn in place. Used by finalizeTurn to swap an
   *  optimistic `pending` turn for the real response without an append (so the
   *  user bubble that was already on screen stays put and the emma bubble
   *  transitions thinking → result). No-op on empty history. */
  replaceLastTurn(entry: TurnEntry): void {
    const h = this.state.turnHistory;
    if (h.length === 0) return;
    this.state = {
      ...this.state,
      turnHistory: [...h.slice(0, -1), entry],
    };
    this.notify();
  }

  /** Replace the turn at `index` in place. Used by the doc-card excerpt
   *  upgrade: the card is pushed immediately with the manifest description,
   *  and the BFF excerpt (which may land after the user already asked a
   *  follow-up) patches THAT turn, not whichever turn is last. Out-of-range
   *  index is a no-op. */
  replaceTurnAt(index: number, entry: TurnEntry): void {
    const h = this.state.turnHistory;
    if (index < 0 || index >= h.length) return;
    this.state = {
      ...this.state,
      turnHistory: [...h.slice(0, index), entry, ...h.slice(index + 1)],
    };
    this.notify();
  }

  /** Drop a trailing optimistic `pending` turn if one is present (e.g. orphaned
   *  by an aborted in-flight run). Safe no-op otherwise. Keeps rapid re-submits
   *  from stacking pending turns. */
  dropTrailingPendingTurn(): void {
    const h = this.state.turnHistory;
    if (h.length === 0 || !h[h.length - 1]!.pending) return;
    this.state = { ...this.state, turnHistory: h.slice(0, -1) };
    this.notify();
  }

  /** Clear turn history. Used by showResponse before the first turn renders. */
  resetTurnHistory(): void {
    this.state = { ...this.state, turnHistory: [] };
    this.notify();
  }

  setLastResolvedParams(params: SearchParams | null): void {
    this.state = { ...this.state, lastResolvedParams: params };
    this.notify();
  }

  setLastMode(mode: ChatMode): void {
    this.state = { ...this.state, lastMode: mode };
    this.notify();
  }

  setLastPickedIDs(ids: string[]): void {
    this.state = { ...this.state, lastPickedIDs: ids.slice() };
    this.notify();
  }

  setSubmitting(value: boolean): void {
    this.state = { ...this.state, isSubmitting: value };
    this.notify();
  }

  markSignupHintShown(): void {
    if (this.state.signupHintShown) return;
    this.state = { ...this.state, signupHintShown: true };
    this.notify();
  }

  setSubmitTimestamp(ts: number): void {
    this.state = { ...this.state, submitTimestamp: ts };
    this.notify();
  }

  /** Set the user-selected card ID. Pass null to clear selection (revert
   *  to per-turn best-price highlight). Only one card can be selected
   *  across the entire conversation. */
  setSelectedCardId(id: string | null): void {
    if (this.state.selectedCardId === id) return;
    this.state = { ...this.state, selectedCardId: id };
    this.notify();
  }

  /** Set carry-deploy mode. Called by startWithConfig ('config') /
   *  startWithNavigation ('navigate'), and cleared (null) on first
   *  trusted user submit/refine so subsequent turns use standard timing. */
  setCarryDeployMode(mode: 'config' | 'navigate' | null): void {
    if (this.state.carryDeployMode === mode) return;
    this.state = { ...this.state, carryDeployMode: mode };
    this.notify();
  }

  /**
   * Reset to initial state (resetToInput equivalent — user clicked clear,
   * pressed Escape, or session reset). Capabilities are preserved — they
   * don't change just because a user reset the conversation.
   */
  reset(): void {
    const preservedCapabilities = this.state.capabilities;
    this.state = { ...initialState(), capabilities: preservedCapabilities };
    this.notify();
  }

  /**
   * Helper: check whether a feature flag is enabled. Returns false if
   * capabilities not yet loaded — components should treat unloaded as
   * "anonymous mode".
   */
  hasFeature(feature: string): boolean {
    return this.state.capabilities?.features.includes(feature) ?? false;
  }

  // --- Internal ---

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
